import { Button, notification, Tooltip, Table, Avatar, Select } from 'antd';
import { useTranslation } from 'react-i18next';
import TableTip from '../../../../Template/layoutComponents/Table/components/tip';
import { CheckCircleOutlined, ExportOutlined, CommentOutlined, EyeOutlined } from '@ant-design/icons';

import './style.css';
import { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';

import useTemplateState from '../../../../Template/atoms/variables';
import useGlobalState from '../../../../Template/atoms/variables';
import {
  getIssues,
  downloadIssuesExport,
  approveIssues,
} from '../../../helpers/apiCalls';
import { useMsal } from '@azure/msal-react';

import { parseIsoDateString } from '../../../../helpers/misc';
import CommentModal from '../../../components/CommentModal';
import IssueDetailsModal from '../Details/components/CommissioningTab/CriteriaTypes/WebFormOpenner/IssueDetailsModal';

const Issues = () => {
  const { t } = useTranslation();
  const { plantId } = useParams();
  const { instance } = useMsal();

  const [notificationApi, contextHolder] = notification.useNotification();

  const {
    setPendingApiCalls,
    setModalContent,
    setModalTitle,
    setModalStyle,
    setModalConfirm,
    setModalOpen,
    setCleanOnCancel,
  } = useTemplateState();

  const { user } = useGlobalState();

  const [issues, setIssues] = useState(null);
  const [selectedIssues, setSelectedIssues] = useState([]);


  const [loading, setLoading] = useState(false);
  const [appliedFilters, setAppliedFilters] = useState({});
  const [groupBy, setGroupBy] = useState(null);

  const finalFilter = { 'subject.location': plantId };

  const getInitials = (email) => {
    if (!email) return '?';
    const parts = email.split('@')[0].split('.');
    if (parts.length >= 2) {
      return (parts[0].charAt(0) + parts[1].charAt(0)).toUpperCase();
    }
    return email.charAt(0).toUpperCase();
  };

  const formatCompactDate = (dateString) => {
    if (!dateString) return '-';
    const date = new Date(dateString);
    const months = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
    return `${months[date.getMonth()]} ${date.getDate()}`;
  };

  const getGroupValue = (item, groupBy) => {
    switch (groupBy) {
      case 'stage':
        return item.stage || 'No Stage';
      case 'executor':
        return item.executor || 'No Executor';
      case 'manufacturer':
        return item.manufacturer || 'No Manufacturer';
      case 'discipline':
        return item.discipline || 'No Discipline';
      case 'status':
        return item.status || 'No Status';
      default:
        return 'Ungrouped';
    }
  };

  const getGroupedData = () => {
    if (!groupBy || !issues?.items) {
      return getFilteredItems();
    }

    const filteredItems = getFilteredItems();
    const grouped = {};
    
    filteredItems.forEach(item => {
      const groupValue = getGroupValue(item, groupBy);
      if (!grouped[groupValue]) {
        grouped[groupValue] = [];
      }
      grouped[groupValue].push(item);
    });

    const result = [];
    Object.entries(grouped).forEach(([groupKey, items]) => {
      // Add group header
      result.push({
        _id: `group_${groupKey}`,
        isGroupHeader: true,
        groupKey,
        groupCount: items.length
      });
      // Add group items
      result.push(...items);
    });

    return result;
  };

  const getFilteredItems = () => {
    if (!issues?.items) return [];
    
    // Check if any filters are actually applied (have values)
    const hasActiveFilters = Object.values(appliedFilters).some(filterValues => 
      filterValues && filterValues.length > 0
    );
    
    if (!hasActiveFilters) {
      return issues.items; // Return all items if no filters
    }

    return issues.items.filter(item => {
      return Object.entries(appliedFilters).every(([columnKey, filterValues]) => {
        if (!filterValues || filterValues.length === 0) return true;
        
        switch (columnKey) {
          case 'stage':
            return filterValues.includes(item.stage);
          case 'subject':
            return filterValues.includes(item.subject);
          case 'manufacturer':
            return filterValues.includes(item.manufacturer);
          case 'category':
            return filterValues.includes(item.category);
          case 'discipline':
            return filterValues.includes(item.discipline);
          case 'status':
            return filterValues.includes(item.status);
          case 'executor':
            return filterValues.includes(item.executor);
          case 'title':
            return filterValues.some(filterValue => 
              item.title?.toLowerCase().includes(filterValue.toLowerCase())
            );
          default:
            return true;
        }
      });
    });
  };

  const getFilteredItemCount = () => {
    const filteredItems = getFilteredItems();
    const hasActiveFilters = Object.values(appliedFilters).some(filterValues => 
      filterValues && filterValues.length > 0
    );
    
    return hasActiveFilters ? filteredItems.length : null;
  };

  const generateCSV = (data) => {
    if (!data || data.length === 0) return '';

    // Define CSV headers
    const headers = [
      'Stage',
      'Title', 
      'Subject',
      'Manufacturer',
      'Category',
      'Discipline',
      'Due Date',
      'Status',
      'Executor',
      'Created At'
    ];

    // Create CSV rows
    const rows = data.map(item => [
      item.stage || '',
      item.title || '',
      item.subject || '',
      item.manufacturer || '',
      item.category || '',
      item.discipline || '',
      item.dueDate || '',
      item.status || '',
      item.executor || '',
      item.createdAt || ''
    ]);

    // Combine headers and rows
    const csvContent = [headers, ...rows]
      .map(row => row.map(field => `"${String(field).replace(/"/g, '""')}"`).join(','))
      .join('\n');

    return csvContent;
  };

  const downloadCSV = (csvContent, filename) => {
    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement('a');
    const url = URL.createObjectURL(blob);
    
    link.setAttribute('href', url);
    link.setAttribute('download', filename);
    link.style.visibility = 'hidden';
    
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };




  useEffect(() => {
    triggerApiCalls();
  }, []);






  const triggerApiCalls = async () => {
    try {
      setPendingApiCalls((current) => current.concat(['getIssues']));
      setLoading(true);

      const apiParams = { ...finalFilter, uniqueValues: true, v2: true };

      const response = await getIssues(instance, apiParams);
      setIssues(response);
    } catch (err) {
      notificationApi.error({
        message: t('database_communication_failure'),
        description: err?.response?.data?.message
          ? `${err?.response?.data?.message} (${err?.response?.status})`
          : err?.message,
        duration: 5,
      });
      console.log(err);
    } finally {
      setPendingApiCalls((current) => current?.filter((item) => item !== 'getIssues'));
      setLoading(false);
    }
  };

  const handleTableChange = (_, filters) => {
    setAppliedFilters(filters);
  };

  const groupingOptions = [
    { value: null, label: 'No grouping' },
    { value: 'stage', label: 'Group by Stage' },
    { value: 'executor', label: 'Group by Executor' },
    { value: 'manufacturer', label: 'Group by Manufacturer' },
    { value: 'discipline', label: 'Group by Discipline' },
    { value: 'status', label: 'Group by Status' }
  ];


  const handleApproveIssues = async () => {
    try {
      setPendingApiCalls((current) => current.concat(['approveIssues']));
      let ids = selectedIssues.map((t) => t._id);
      const approved_by = user?._id;
      
      await approveIssues(instance, ids, approved_by);

      setIssues((current) => ({
        ...current,
        items: current.items.map((issue) =>
          ids.some((id) => id === issue._id) ? { ...issue, status: 'Approved' } : issue,
        ),
      }));
      setSelectedIssues([]);
      triggerApiCalls();
    } catch (error) {
      console.error('Error approving issues:', error);
    } finally {
      setPendingApiCalls((current) => current.filter((item) => item !== 'approveIssues'));
    }
  };

  const handleExportTasks = async () => {
    try {
      setPendingApiCalls((current) => current.concat(['exportTasks']));
      
      // Check if there are active filters
      const hasActiveFilters = Object.values(appliedFilters).some(filterValues => 
        filterValues && filterValues.length > 0
      );

      if (hasActiveFilters) {
        // Export filtered data as CSV
        const filteredItems = getFilteredItems();
        const csvContent = generateCSV(filteredItems);
        const timestamp = new Date().toISOString().split('T')[0];
        const filename = `issues_filtered_${timestamp}.csv`;
        downloadCSV(csvContent, filename);
      } else {
        // Use existing backend export functionality for all data
        await downloadIssuesExport(instance, plantId, finalFilter);
      }
    } catch (error) {
      console.error('Error exporting tasks:', error);
      notificationApi.error({
        message: t('export_error') || 'Export Error',
        description: error?.message || 'Failed to export issues',
        duration: 5,
      });
    } finally {
      setPendingApiCalls((current) => current.filter((item) => item !== 'exportTasks'));
    }
  };

  const getApproveTooltip = () => {
    if (!selectedIssues.length) return t('select_issues_to_approve');
  };

  const handleOpenIssueDetails = (issue) => {
    setModalContent(
      <IssueDetailsModal
        givenIssue={issue}
        handleInsertComment={() => handleInsertComment(issue)}
      />,
    );
    setModalTitle(t('issue'));
    setModalStyle({
      padding: '2rem',
      gap: '1rem',
      maxHeight: '100%',
    });
    setModalConfirm(null);
    setCleanOnCancel(true);
    setModalOpen(true);
  };

  const handleInsertComment = (issue) => {
    setModalContent(<CommentModal modelName={'Issue'} related_to={issue._id} />);
    setModalTitle(t('comments'));
    setModalStyle({
      padding: '2rem',
      gap: '1rem',
      maxHeight: '100%',
    });
    setModalConfirm(null);
    setCleanOnCancel(true);
    setModalOpen(true);
  };


  const columns = [
    {
      title: t('stage'),
      dataIndex: 'stage',
      key: 'stage',
      width: '6%',
      filters: issues?.uniqueValues?.stage?.map(value => ({ text: value, value })) || 
               [...new Set(issues?.items?.map(item => item.stage).filter(Boolean))].map(value => ({ text: value, value })) || [],
      onFilter: (value, record) => record.stage === value,
      ellipsis: true,
      render: (text, record) => {
        if (record.isGroupHeader) return null;
        return (
          <Tooltip title={text}>
            <span>{text || '-'}</span>
          </Tooltip>
        );
      },
    },
    {
      title: t('title'),
      dataIndex: 'title',
      key: 'title',
      width: '38%',
      ellipsis: true,
      filterDropdown: ({ setSelectedKeys, selectedKeys, confirm, clearFilters }) => (
        <div style={{ padding: 8 }}>
          <input
            placeholder={`Search ${t('title')}`}
            value={selectedKeys[0]}
            onChange={e => setSelectedKeys(e.target.value ? [e.target.value] : [])}
            onPressEnter={() => confirm()}
            style={{ marginBottom: 8, display: 'block' }}
          />
          <Button
            type="primary"
            onClick={() => confirm()}
            size="small"
            style={{ width: 90, marginRight: 8 }}
          >
            Search
          </Button>
          <Button onClick={() => clearFilters()} size="small" style={{ width: 90 }}>
            Reset
          </Button>
        </div>
      ),
      filterIcon: filtered => <span style={{ color: filtered ? '#1890ff' : undefined }}>🔍</span>,
      onFilter: (value, record) => record.title?.toLowerCase().includes(value.toLowerCase()),
      render: (text, record) => {
        if (record.isGroupHeader) {
          return (
            <div style={{ 
              fontWeight: 'bold',
              fontSize: '14px',
              padding: '8px 0',
              color: '#1777FF',
              backgroundColor: '#f0f6ff',
              borderRadius: '4px',
              paddingLeft: '8px'
            }}>
              {record.groupKey} ({record.groupCount} items)
            </div>
          );
        }
        return (
          <div style={{ 
            display: '-webkit-box',
            WebkitLineClamp: 2,
            WebkitBoxOrient: 'vertical',
            overflow: 'hidden',
            lineHeight: '1.3',
            maxHeight: '2.6em'
          }}>
            {text}
          </div>
        );
      },
    },
    {
      title: t('subject'),
      dataIndex: 'subject',
      key: 'subject',
      width: '12%',
      ellipsis: true,
      filters: issues?.uniqueValues?.subject?.map(value => ({ text: value, value })) || 
               [...new Set(issues?.items?.map(item => item.subject).filter(Boolean))].map(value => ({ text: value, value })) || [],
      onFilter: (value, record) => record.subject === value,
      render: (_, record) => {
        if (record.isGroupHeader) return null;
        return (
          <Tooltip title={record.subject}>
            <span>{record.subject || '-'}</span>
          </Tooltip>
        );
      },
    },
    {
      title: t('manufacturer'),
      dataIndex: 'manufacturer',
      key: 'manufacturer',
      width: '8%',
      ellipsis: true,
      filters: issues?.uniqueValues?.manufacturer?.map(value => ({ text: value, value })) || 
               [...new Set(issues?.items?.map(item => item.manufacturer).filter(Boolean))].map(value => ({ text: value, value })) || [],
      onFilter: (value, record) => record.manufacturer === value,
      render: (_, record) => {
        if (record.isGroupHeader) return null;
        return (
          <Tooltip title={record.manufacturer}>
            <span>{record.manufacturer || '-'}</span>
          </Tooltip>
        );
      },
    },
    {
      title: t('category'),
      dataIndex: 'category',
      key: 'category',
      width: '8%',
      ellipsis: true,
      filters: issues?.uniqueValues?.category?.map(value => ({ text: value, value })) || 
               [...new Set(issues?.items?.map(item => item.category).filter(Boolean))].map(value => ({ text: value, value })) || [],
      onFilter: (value, record) => record.category === value,
      render: (_, record) => {
        if (record.isGroupHeader) return null;
        return (
          <Tooltip title={record.category}>
            <span>{record.category || '-'}</span>
          </Tooltip>
        );
      },
    },
    {
      title: t('discipline'),
      dataIndex: 'discipline',
      key: 'discipline',
      width: '8%',
      ellipsis: true,
      filters: issues?.uniqueValues?.discipline?.map(value => ({ text: value, value })) || 
               [...new Set(issues?.items?.map(item => item.discipline).filter(Boolean))].map(value => ({ text: value, value })) || [],
      onFilter: (value, record) => record.discipline === value,
      render: (_, record) => {
        if (record.isGroupHeader) return null;
        return (
          <Tooltip title={record.discipline}>
            <span>{record.discipline || '-'}</span>
          </Tooltip>
        );
      },
    },
    // {
    //   title: t('created'),
    //   dataIndex: 'createdAt',
    //   key: 'createdAt',
    //   width: '7%',
    //   sorter: (a, b) => new Date(a.createdAt) - new Date(b.createdAt),
    //   render: (text, record) => {
    //     if (record.isGroupHeader) return null;
    //     return (
    //       <Tooltip title={parseIsoDateString(text)}>
    //         <span>{formatCompactDate(text)}</span>
    //       </Tooltip>
    //     );
    //   },
    // },
    {
      title: t('due'),
      dataIndex: 'dueDate',
      key: 'dueDate',
      width: '8%',
      sorter: (a, b) => new Date(a.dueDate) - new Date(b.dueDate),
      render: (text, record) => {
        if (record.isGroupHeader) return null;
        return (
          <Tooltip title={parseIsoDateString(text)}>
            <span>{formatCompactDate(text)}</span>
          </Tooltip>
        );
      },
    },
    {
      title: t('status'),
      dataIndex: 'status',
      key: 'status',
      width: '8%',
      filters: issues?.uniqueValues?.status?.map(value => ({ text: value, value })) || [],
      onFilter: (value, record) => record.status === value,
      render: (text) => (
        <div style={{ whiteSpace: 'nowrap' }}>
          <TableTip>{text}</TableTip>
        </div>
      ),
    },
    {
      title: t('executor'),
      dataIndex: 'executor',
      key: 'executor',
      width: '7%',
      align: 'center',
      filters: issues?.uniqueValues?.executor?.map(value => ({ text: value, value })) || 
               [...new Set(issues?.items?.map(item => item.executor).filter(Boolean))].map(value => ({ text: value, value })) || [],
      onFilter: (value, record) => record.executor === value,
      render: (_, record) => {
        if (record.isGroupHeader) return null;
        if (!record.executor) {
          return (
            <Tooltip title="No executor assigned">
              <Avatar size="small" style={{ backgroundColor: '#ccc' }}>?</Avatar>
            </Tooltip>
          );
        }
        return (
          <Tooltip title={record.executor}>
            <Avatar 
              size="small" 
              style={{ 
                backgroundColor: '#1777FF',
                color: 'white',
                fontSize: '10px'
              }}
            >
              {getInitials(record.executor)}
            </Avatar>
          </Tooltip>
        );
      },
    },
    {
      title: t('action'),
      key: 'action',
      fixed: 'right',
      width: '6%',
      render: (_, record) => {
        if (record.isGroupHeader) return null;
        return (
          <div style={{ display: 'flex', gap: '4px', justifyContent: 'center' }}>
            <Tooltip title={t('add_comment')}>
              <Button
                type="text"
                icon={<CommentOutlined />}
                size="small"
                style={{
                  color: '#1777FF',
                  padding: '2px',
                  minWidth: '20px',
                  height: '20px',
                }}
                onClick={() => handleInsertComment(record)}
              />
            </Tooltip>
            <Tooltip title={t('view_details')}>
              <Button
                type="text"
                icon={<EyeOutlined />}
                size="small"
                style={{
                  color: record?.status?.toLowerCase() === 'approved' ? '#ccc' : '#1777FF',
                  padding: '2px',
                  minWidth: '20px',
                  height: '20px',
                }}
                disabled={record?.status?.toLowerCase() === 'approved'}
                onClick={() => handleOpenIssueDetails(record)}
              />
            </Tooltip>
          </div>
        );
      },
    },
  ];

  return (
    <>
      {contextHolder}
      <div id='issues'>

        <div className='pageContentBlock' style={{ gap: '1rem' }}>
          <div className='pageContentHeader'>
            <div className='pageTitle'>{t('issues')}</div>
            <div className='headerButtons'>
              <Button
                size='large'
                icon={<ExportOutlined />}
                style={{
                  borderColor: '#242424',
                  background: '#242424',
                  color: 'white',
                  opacity: loading ? 0.5 : 1,
                  marginRight: '8px',
                }}
                disabled={loading}
                onClick={handleExportTasks}
              >
                {t('export')}
              </Button>
              <Tooltip title={getApproveTooltip()}>
                <Button
                  disabled={selectedIssues.length === 0}
                  size='large'
                  style={{
                    borderColor: '#52C41A',
                    background: '#52C41A',
                    color: 'white',
                    opacity: selectedIssues.length !== 0 ? 1 : 0.5,
                  }}
                  icon={<CheckCircleOutlined />}
                  onClick={() => handleApproveIssues(selectedIssues)}
                >
                  {t('approve')}
                </Button>
              </Tooltip>
            </div>
          </div>
          {getFilteredItemCount() !== null && (
            <div style={{ 
              marginBottom: '12px', 
              padding: '8px 12px', 
              backgroundColor: '#f0f6ff', 
              border: '1px solid #d6e4ff',
              borderRadius: '6px',
              color: '#1777FF',
              fontSize: '14px',
              fontWeight: '500'
            }}>
              📊 Showing {getFilteredItemCount()} of {issues?.items?.length || 0} issues (filtered)
            </div>
          )}
          <div style={{ marginBottom: '16px', display: 'flex', alignItems: 'center', gap: '12px' }}>
            <span style={{ fontWeight: '500', color: '#333' }}>Group by:</span>
            <Select
              style={{ width: 200 }}
              value={groupBy}
              onChange={setGroupBy}
              options={groupingOptions}
              placeholder="Select grouping"
            />
          </div>
          <Table
            columns={columns}
            dataSource={getGroupedData()}
            rowKey="_id"
            loading={loading}
            size="small"
            onChange={handleTableChange}
            rowSelection={{
              selectedRowKeys: selectedIssues.map(item => item._id),
              onChange: (_, selectedRows) => {
                setSelectedIssues(selectedRows);
              },
            }}
            pagination={false}
          />
        </div>
      </div>
    </>
  );
};

export default Issues;
